import axios from "axios";
import { APP_VERSION } from "@/lib/api_client";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
export const BACKEND_ORIGIN = BACKEND_URL;

const client = axios.create({ baseURL: API });

// Tag every request with the running frontend build so backend access
// logs and the operator dashboard can correlate any reported bug back
// to the exact build that produced it. Kept on a per-client interceptor
// because this `client` is used for typed owner-API helpers that DO
// throw on errors (different contract than the shared api_client.js).
client.interceptors.request.use((config) => {
  config.headers = config.headers || {};
  config.headers["X-FeedPulse-Client"] = APP_VERSION;
  return config;
});

// --- Sites ---
export const createSite = (domain) => client.post("/sites", { domain }).then((r) => r.data);
export const getSite = (id) => client.get(`/sites/${id}`).then((r) => r.data);
export const updateSite = (id, payload) => client.patch(`/sites/${id}`, payload).then((r) => r.data);
export const seedDemo = (id, count = 1) => client.post(`/demo/seed/${id}`, null, { params: { count } }).then((r) => r.data);

// --- Widget data (public) ---
export const getTraffic = (id, limit = 10) => client.get(`/widget/traffic/${id}`, { params: { limit } }).then((r) => r.data);
export const getFlags = (id) => client.get(`/widget/flags/${id}`).then((r) => r.data);
export const getOnline = (id) => client.get(`/widget/online/${id}`).then((r) => r.data);

// --- Owner / magic link ---
export const claimSite = (id, email) => client.post(`/sites/${id}/claim`, { email }).then((r) => r.data);
export const ownerMe = (token) => client.get(`/owner/me`, { params: { token } }).then((r) => r.data);

// --- Stats (token-gated) ---
export const statsOverview = (id, token, range = "24h") => client.get(`/stats/${id}/overview`, { params: { token, range } }).then((r) => r.data);
export const statsTimeseries = (id, token, range = "24h") => client.get(`/stats/${id}/timeseries`, { params: { token, range } }).then((r) => r.data);
export const statsReferrers = (id, token, range = "24h") => client.get(`/stats/${id}/referrers`, { params: { token, range } }).then((r) => r.data);
export const statsPages = (id, token, range = "24h") => client.get(`/stats/${id}/pages`, { params: { token, range } }).then((r) => r.data);
export const statsDevices = (id, token, range = "24h") => client.get(`/stats/${id}/devices`, { params: { token, range } }).then((r) => r.data);
export const statsEngagement = (id, token) => client.get(`/stats/${id}/engagement`, { params: { token } }).then((r) => r.data);
export const statsHosts = (id, token, range = "24h") => client.get(`/stats/${id}/hosts`, { params: { token, range } }).then((r) => r.data);

// --- Alerts ---
export const listAlerts = (id, token) => client.get(`/alerts/${id}`, { params: { token } }).then((r) => r.data);
export const createAlert = (id, token, rule) => client.post(`/alerts/${id}`, rule, { params: { token } }).then((r) => r.data);
export const deleteAlert = (id, token, ruleId) => client.delete(`/alerts/${id}/${ruleId}`, { params: { token } }).then((r) => r.data);
export const recentAlertEvents = (id, token, limit = 20) => client.get(`/alerts/${id}/recent`, { params: { token, limit } }).then((r) => r.data);

// --- Public stats page ---
export const publicStats = (slug) => client.get(`/public/site/${slug}`).then((r) => r.data);


// --- Alternative ownership verification (item 9) ---
export const verificationInstructions = (siteId) => client.get(`/sites/${siteId}/verify/instructions`).then((r) => r.data);
export const verifyOwnership = (siteId, method, email) =>
  client.post(`/sites/${siteId}/verify`, { method, email: email || null }).then((r) => r.data);

// --- Auto-palette: extract colors from the customer's actual site ---
export const autoPalette = (siteId) => client.get(`/sites/${siteId}/auto-palette`).then((r) => r.data);

// --- Ad slots (item 12) ---
export const getAdSlot = (position) => client.get(`/ads/${position}`).then((r) => r.data);
